import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { TemplatePortal } from '@angular/cdk/portal';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';

@Component({
  selector: 'app-paginator-selector',
  standalone: true,
  imports: [MatIcon, MatIconModule, CommonModule],
  templateUrl: './paginator-selector.component.html',
  styleUrl: './paginator-selector.component.scss'
})
export class PaginatorSelectorComponent {
  @Input() items: string[] = [];
  @Input() selected: string = '';
  @Output() selectionChange = new EventEmitter<string>();

  @ViewChild('selectorContainer', { static: false, read: ElementRef }) selectorRef!: ElementRef;
  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<any>;

  overlayRef: OverlayRef | null = null;
  isOpen: boolean = false;
  dropdownPosition: 'above' | 'below' = 'below';

  constructor(
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
  ) {}

  toggleDropdown(event: UIEvent): void {
    event.stopPropagation();
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown(): void {
    // Calculate available space and the height that the dropdown will occupy
    const rect = this.selectorRef.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = Math.min(200, this.items.length * 40);
    
    let positions: ConnectedPosition[];
    if (spaceBelow < dropdownHeight) {
      positions = [
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: 0,
        },
      ];
      this.dropdownPosition = 'above';
    } else {
      positions = [
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 0,
        },
      ];
      this.dropdownPosition = 'below';
    }

    const positionStrategy = this.overlay.position().flexibleConnectedTo(this.selectorRef).withPositions(positions);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'custom-overlay-pane',
    });

    this.overlayRef.backdropClick().subscribe(() => this.closeDropdown());
    const templatePortal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(templatePortal);

    // Assign the overlay the same width as the container
    const containerWidth = this.selectorRef.nativeElement.getBoundingClientRect().width;
    this.overlayRef.overlayElement.style.width = `${containerWidth}px`;

    this.isOpen = true;
  }

  closeDropdown(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.isOpen = false;
  }

  selectItem(item: string, event: UIEvent): void {
    event.stopPropagation();
    this.selected = item;
    this.isOpen = false;
    this.selectionChange.emit(item);
    this.closeDropdown();
  }

  computeDropdownPosition(): void {
    if (this.selectorRef) {
      const rect = this.selectorRef.nativeElement.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = Math.min(200, this.items.length * 40);
      this.dropdownPosition = spaceBelow < dropdownHeight ? 'above' : 'below';
    }
  }

  ngAfterViewInit(): void {
    //document.addEventListener('click', this.handleClickOutside.bind(this));
  }

  handleClickOutside(event: MouseEvent): void {
    if (this.selectorRef && !this.selectorRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }
}
